import React, { useContext, useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Button,
    Typography,
    Container,
    Box,
    Grid,
    CircularProgress,
    Snackbar,
    Alert,
} from '@mui/material';
import { Business, CheckCircle } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { AppContext } from '../../AppContext';
import { useSaveUserSettings } from '../../shared/hooks/useFrappeDoctype';
import { useListDocTypes } from '../../shared/hooks/useListDocTypesData';

const useStyles = makeStyles(() => ({
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f6fa',
        padding: '16px',
    },
    title: {
        textAlign: 'center',
        marginBottom: '32px',
        fontWeight: 'bold',
        color: '#271D1C',
    },
    subtitle: {
        textAlign: 'center',
        marginBottom: '32px',
        color: '#6E7187',
    },
    companyCard: {
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '2px solid transparent',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.15)',
        },
    },
    selectedCard: {
        border: '2px solid #e14954',
        transform: 'translateY(-2px)',
        boxShadow: '0px 8px 25px rgba(225, 73, 84, 0.3)',
    },
    companyIcon: {
        width: 48,
        height: 48,
        backgroundColor: '#e14954',
        color: 'white',
        margin: '0 auto',
        marginBottom: '16px',
    },
    companyName: {
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '8px',
        color: '#271D1C',
    },
    companyDetails: {
        textAlign: 'center',
        color: '#6E7187',
        fontSize: '0.875rem',
    },
    continueButton: {
        background: '#e14954',
        color: 'white',
        padding: '12px 32px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        borderRadius: '8px',
        boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.07), 0px 4px 18px rgba(249, 98, 83, 0.25)',
        '&:hover': {
            background: '#d13a45',
        },
        '&:disabled': {
            background: '#ccc',
            color: '#666',
        },
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '32px',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
    },
}));

function CompanyProfileSelection() {
    const classes = useStyles();
    const { userInfo } = useContext(AppContext);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const { data: companies, isLoading: companiesLoading } = useListDocTypes(
        "getCompanies",
        "Company",
        "",
        {},
        ["*"]
    );

    const saveUserSettings = useSaveUserSettings();

    const handleCompanySelect = (company) => {
        setSelectedCompany(company);
    };

    const handleContinue = async () => {
        if (!selectedCompany) return;

        setIsLoading(true);
        try {
            await saveUserSettings.mutateAsync({
                doctype: "Company",
                user_settings: {
                    selectedProfile: selectedCompany.name,
                },
            });

            setSnackbar({
                open: true,
                message: `${selectedCompany.company_name} selected successfully!`,
                severity: 'success',
            });

            // Reload the page to trigger AppContext to pick up the new company selection
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        } catch (error) {
            console.error('Error saving company selection:', error);
            setSnackbar({
                open: true,
                message: 'Failed to save company selection. Please try again.',
                severity: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (companiesLoading) {
        return (
            <Container className={classes.container}>
                <div className={classes.loadingContainer}>
                    <CircularProgress size={60} style={{ color: '#e14954' }} />
                </div>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" className={classes.container}>
            <Box>
                <Typography variant="h4" className={classes.title}>
                    Welcome to POS System
                </Typography>
                <Typography variant="h6" className={classes.subtitle}>
                    Hello {userInfo?.full_name}, please select your company profile to continue
                </Typography>

                <Grid container spacing={3} justifyContent="center">
                    {companies?.map((company) => (
                        <Grid item xs={12} sm={6} md={4} key={company.name}>
                            <Card
                                className={`${classes.companyCard} ${
                                    selectedCompany?.name === company.name ? classes.selectedCard : ''
                                }`}
                                onClick={() => handleCompanySelect(company)}
                                elevation={selectedCompany?.name === company.name ? 8 : 2}
                            >
                                <CardContent>
                                    <Box display="flex" flexDirection="column" alignItems="center" p={2}>
                                        <Box className={classes.companyIcon} display="flex" alignItems="center" justifyContent="center" borderRadius="50%">
                                            {selectedCompany?.name === company.name ? (
                                                <CheckCircle />
                                            ) : (
                                                <Business />
                                            )}
                                        </Box>
                                        <Typography variant="h6" className={classes.companyName}>
                                            {company.company_name || company.name}
                                        </Typography>
                                        {company.domain && (
                                            <Typography className={classes.companyDetails}>
                                                {company.domain}
                                            </Typography>
                                        )}
                                        {company.country && (
                                            <Typography className={classes.companyDetails}>
                                                {company.country}
                                            </Typography>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <div className={classes.buttonContainer}>
                    <Button
                        className={classes.continueButton}
                        disabled={!selectedCompany || isLoading}
                        onClick={handleContinue}
                    >
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Continue to POS'}
                    </Button>
                </div>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default CompanyProfileSelection;